import { useEffect, useRef } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useCan, useSession } from "../lib/session";

const NAV: Array<{ to: string; label: string; capability?: string }> = [
  { to: "/", label: "Home" },
  { to: "/research", label: "Research" },
  { to: "/agents", label: "Agents", capability: "workflows.read" },
  { to: "/monitors", label: "Monitors", capability: "workflows.read" },
  { to: "/companies", label: "Companies" },
  { to: "/documents", label: "Documents", capability: "research.read" },
  { to: "/watchlists", label: "Watchlists", capability: "research.read" },
  { to: "/digests", label: "Research alerts", capability: "research.read" },
  { to: "/portfolio", label: "Portfolio", capability: "portfolio.read" },
  { to: "/operations", label: "Operations", capability: "operations.read" },
];

function NavItem({ to, label, capability }: (typeof NAV)[number]) {
  const allowed = useCan(capability ?? "") || !capability;
  const { pathname } = useLocation();
  const linkRef = useRef<HTMLAnchorElement>(null);
  const isCurrent = to === "/" ? pathname === "/" : pathname === to || pathname.startsWith(`${to}/`);

  useEffect(() => {
    const link = linkRef.current;
    if (
      !isCurrent ||
      !link ||
      typeof link.scrollIntoView !== "function" ||
      typeof window.matchMedia !== "function" ||
      !window.matchMedia("(max-width: 700px)").matches
    ) {
      return;
    }
    link.scrollIntoView({ block: "nearest", inline: "center" });
  }, [isCurrent]);

  if (capability && !allowed) {
    return null;
  }
  return (
    <NavLink
      ref={linkRef}
      to={to}
      end={to === "/"}
      className={({ isActive }) => (isActive ? "active" : "")}
    >
      {label}
    </NavLink>
  );
}

export default function Shell() {
  const session = useSession();
  return (
    <>
      <header className="topbar">
        <div className="topbar-inner">
          <div className="topbar-primary">
            <div className="brand">
              <b>Athena</b>
              <span className="brand-context">Research operations</span>
            </div>
            <div className="identity" title={session.email}>
              <span className="identity-label">Signed in</span>
              <span>{session.name ?? session.email}</span>
            </div>
          </div>
          <nav className="mainnav" aria-label="Primary navigation">
            {NAV.map((item) => (
              <NavItem key={item.to} {...item} />
            ))}
          </nav>
        </div>
      </header>
      <main className="page">
        <Outlet />
      </main>
    </>
  );
}
